import {useEffect, useMemo, useState} from 'react';

export interface UseDeviceOptions {
   /** Width below which the viewport is considered "small". */
   mobileBreakpoint?: number;
}

export interface DeviceInfo {
   /** Device supports touch input. */
   isTouch: boolean;

   /** Primary pointer is coarse (typically a finger). */
   isCoarsePointer: boolean;

   /** Primary pointer is fine (typically a pointing device). */
   isFinePointer: boolean;

   /** Primary pointer supports hover. */
   canHover: boolean;

   /** Current viewport is below the breakpoint. */
   isSmallViewport: boolean;

   /** Convenience for phones and tablets. */
   isPhoneOrTablet: boolean;

   /** iPhone/iPad/iPod (including iPadOS pretending to be a Mac). */
   isIOS: boolean;
}

export function useDevice({mobileBreakpoint = 768}: UseDeviceOptions = {}): DeviceInfo {
   const [isSmallViewport, setIsSmallViewport] = useState(() =>
      typeof window === 'undefined' ? false : window.matchMedia(`(max-width: ${mobileBreakpoint}px)`).matches,
   );

   useEffect(() => {
      const mediaQuery = window.matchMedia(`(max-width: ${mobileBreakpoint}px)`);

      const handleChange = (event: MediaQueryListEvent) => {
         setIsSmallViewport(event.matches);
      };

      // Keep state synchronized in case the breakpoint changes.
      setIsSmallViewport(mediaQuery.matches);

      mediaQuery.addEventListener('change', handleChange);

      return () => {
         mediaQuery.removeEventListener('change', handleChange);
      };
   }, [mobileBreakpoint]);

   return useMemo(() => {
      // SSR-safe, although you said you're using Vite.
      if (typeof window === 'undefined') {
         return {
            isTouch: false,
            isCoarsePointer: false,
            isFinePointer: false,
            canHover: true,
            isSmallViewport: false,
            isPhoneOrTablet: false,
            isIOS: false,
         };
      }

      const isTouch = navigator.maxTouchPoints > 0;
      const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
      const isFinePointer = window.matchMedia('(pointer: fine)').matches;
      const canHover = window.matchMedia('(hover: hover)').matches;

      const isIOS =
         /iPad|iPhone|iPod/.test(navigator.userAgent) ||
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      return {
         isTouch,
         isCoarsePointer,
         isFinePointer,
         canHover,
         isSmallViewport,
         isPhoneOrTablet: isTouch && isCoarsePointer,
         isIOS,
      };
   }, [isSmallViewport]);
}
